/* Ported from the vhc prototype's kit (src/pages/AIGenerated/vhc/components) — it had
   no equivalent here, so it moved rather than being replaced. */
import clsx from 'clsx';
import { type ReactNode, useRef, useState } from 'react';
import { BiCheck, BiChevronDown } from 'react-icons/bi';

import { AnchoredPopover } from '../AnchoredPopover/AnchoredPopover';

import s from './InlineSelect.module.scss';

export type InlineOption<T extends string> = { value: T; label: ReactNode };

/**
 * A value in a list that can be changed where it sits — the status or priority chip on a
 * task row, say. Not `Select` from `Inputs`: that is a field in a form, with a label and a
 * placeholder and a value it does not yet have. This one already shows the value, and
 * pressing it is how you change it.
 *
 * Changing a status is the most frequent edit anyone makes to a task, and
 * routing it through "open the panel, change the field, press Save" is three
 * interactions for a one-word change. This makes the chip itself the control.
 *
 * The chevron is deliberately NOT always visible: two extra glyphs on every row
 * of a list is exactly the clutter the outlined priority tag was introduced to
 * undo. It appears on hover of the cell, on keyboard focus, and permanently on
 * touch pointers, which have no hover to reveal it with — see the stylesheet.
 *
 * @alias editable chip, inline dropdown, quick edit, change in place
 */
export const InlineSelect = <T extends string>({
  value,
  options,
  onChange,
  /** Names the field for assistive tech: "Priority", "Status". */
  label,
  children,
  className,
  disabled = false,
  valueLabel,
  open: openProp,
  onOpenChange,
}: {
  value: T;
  options: InlineOption<T>[];
  onChange: (value: T) => void;
  label: string;
  /** How assistive tech reads the value, when `value` is an id rather than a word. */
  valueLabel?: string;
  /** The chip as it reads when closed. */
  children: ReactNode;
  className?: string;
  /** Holds the chip shut while a change it made is still being saved. */
  disabled?: boolean;
  /**
   * Hands the menu's open state to the caller, for one that has something to settle
   * first — asking which tasks of a series a change is for, say. Pressing the chip then
   * only asks, through `onOpenChange`; the menu opens when `open` says so.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = openProp ?? innerOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setInnerOpen(next);
    onOpenChange?.(next);
  };
  const anchor = useRef<HTMLButtonElement>(null);
  /* The popover needs the trigger element, and a ref read during render is stale on the
     first paint — and is what `react-hooks` warns about. Holding it in state means the
     popover mounts against a node that exists. */
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={clsx(s.trigger, open && s.triggerOpen, className)}
        aria-label={`${label}: ${valueLabel ?? value}. Change`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-busy={disabled || undefined}
        disabled={disabled}
        /* The row underneath opens the detail panel; a click meant for this
           control must not also count as a click on the row. */
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(anchor.current);
          setOpen(!open);
        }}
      >
        {children}
        <BiChevronDown className={s.caret} aria-hidden="true" />
      </button>

      <AnchoredPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorEl}
        width={180}
        align="start"
        label={label}
      >
        <ul className={s.list} role="listbox" aria-label={label}>
          {options.map((option) => {
            const current = option.value === value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  className={clsx(s.option, current && s.optionCurrent)}
                  role="option"
                  aria-selected={current}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    /* No write when nothing changed: every save here is a store
                       write and a toast, and re-picking the value you are
                       already on is not an edit. */
                    if (!current) onChange(option.value);
                  }}
                >
                  <span className={s.optionLabel}>{option.label}</span>
                  {/* Space for the tick is reserved on every row, so the labels
                      do not shift by 18px when the selection moves. */}
                  <BiCheck className={s.tick} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      </AnchoredPopover>
    </>
  );
};
